import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Alert,
} from '@mui/material'
import { ArrowBack, Email } from '@mui/icons-material'
import Button from '@components/common/Button'
import api from '@services/api'

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: '',
    },
  })

  const onSubmit = async (data) => {
    setLoading(true)
    setError(null)
    try {
      await api.post('/auth/forgot-password/', { email: data.email })
      setSubmitted(true)
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F5F7FA',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 420,
          width: '100%',
          p: 4,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Logo */}
        <Box textAlign="center" mb={4}>
          <Box
            sx={{
              width: 60,
              height: 60,
              backgroundColor: '#1B5E7A',
              borderRadius: 2,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" fontWeight={700} color="white">
              UCI
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Forgot Password
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Enter your email to reset your password
          </Typography>
        </Box>

        {submitted ? (
          <Box textAlign="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#E8F5E9',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Email sx={{ fontSize: 32, color: '#4CAF50' }} />
            </Box>
            <Typography variant="h6" gutterBottom>
              Check Your Email
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              If an account exists with that email, we've sent password reset instructions.
              Please check your inbox and spam folder.
            </Typography>
            <Button
              component={Link}
              to="/login"
              variant="outlined"
              startIcon={<ArrowBack />}
              fullWidth
            >
              Back to Login
            </Button>
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <TextField
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                fullWidth
                label="Email Address"
                type="email"
                error={!!errors.email}
                helperText={errors.email?.message}
                margin="normal"
                autoComplete="email"
                autoFocus
              />

              <Button
                type="submit"
                fullWidth
                loading={loading}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                Send Reset Link
              </Button>
            </form>

            <Box textAlign="center" mt={2}>
              <Button
                component={Link}
                to="/login"
                variant="text"
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none' }}
              >
                Back to Login
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  )
}

export default ForgotPassword
